/**
 * Stepper — horizontal progress through an N-step flow.
 *
 * Used by debranding (Backup → Migrate → Restore), the config-push wizard,
 * and the onboarding flow when those land. Steps render in three states:
 * done (✓), active (gradient ring), upcoming (muted number).
 *
 * Pure presentation — pass the steps array + the currently-active index;
 * earlier steps are auto-marked done.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

export interface StepperStep {
  /** Stable id used as the React key. */
  id: string;
  /** Headline shown under the bullet. */
  title: string;
  /** Optional sub-line (e.g. status). */
  hint?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** 0-based index of the active step. */
  active: number;
  className?: string;
}

export function Stepper({ steps, active, className }: StepperProps) {
  return (
    <div className={cn("flex items-stretch gap-3", className)}>
      {steps.map((step, i) => {
        const state: "done" | "active" | "upcoming" =
          i < active ? "done" : i === active ? "active" : "upcoming";
        const isLast = i === steps.length - 1;
        return (
          <div key={step.id} className="flex flex-1 items-start gap-3">
            <div className="flex flex-col items-center gap-1.5">
              {/* Bullet */}
              <div
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-mono text-sm font-bold",
                  state === "done" &&
                    "bg-mint/15 text-mint border border-mint/40",
                  state === "active" &&
                    "bg-aurora-grad text-bg-0 shadow-[0_8px_24px_rgba(125,108,255,0.4)]",
                  state === "upcoming" &&
                    "border border-line-strong bg-surface text-fg-mute",
                )}
              >
                {state === "done" ? <Check size={16} strokeWidth={3} /> : i + 1}
              </div>
            </div>

            <div className="min-w-0 flex-1 pt-1.5">
              <div
                className={cn(
                  "text-sm font-semibold tracking-tight leading-tight",
                  state === "active" ? "text-fg" : "text-fg-dim",
                  state === "upcoming" && "text-fg-mute",
                )}
              >
                {step.title}
              </div>
              {step.hint ? (
                <div className="mt-0.5 font-mono text-[10.5px] text-fg-mute">{step.hint}</div>
              ) : null}
            </div>

            {/* Connector — drawn after every step except the last. */}
            {!isLast ? (
              <div
                aria-hidden="true"
                className={cn(
                  "mt-4 h-px flex-1 self-start",
                  i < active
                    ? "bg-gradient-to-r from-mint/60 to-violet/60"
                    : "bg-line",
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
